import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../utils/api';
import ProjektorContactForm, {
  tomtProjektorFormular as emptyForm,
  formularFranKontakt,
  sparaProjektorKontakt
} from './ProjektorContactForm';

/**
 * ProjektorContactAdmin — admin-hantering av "mail-only" projektörer (ProjektorContact).
 * Projektörer som är användare hanteras i stället via Användare-fliken.
 *
 * Samma formulär och samma spar-logik som Projektörer-modalen använder, via
 * ProjektorContactForm. Skillnaden är att den här vyn raderar permanent medan
 * modalen bara inaktiverar.
 */

const ProjektorContactAdmin = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projektorer/contacts`, { headers: authHeaders() });
      const data = await res.json();
      setContacts(res.ok && data.success ? data.data : []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setError(''); setShowForm(true); };
  const openEdit = (c) => {
    setEditingId(c._id);
    setForm(formularFranKontakt(c));
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { setError('E-post krävs.'); return; }
    setSaving(true);
    setError('');
    try {
      await sparaProjektorKontakt(editingId, form);
      setShowForm(false);
      await fetchContacts();
    } catch (err) {
      setError(err.message || 'Ett fel uppstod');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Ta bort ${c.companyName || c.email}?`)) return;
    try {
      await fetch(`${API_URL}/api/projektorer/contacts/${c._id}`, { method: 'DELETE', headers: authHeaders() });
      await fetchContacts();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Projektörer (mail-only)</h2>
          <p className="text-sm text-gray-500">Externa specialister som inte är användare men ska kunna kontaktas via "Projektörer". Projektörer som är användare hanteras under Användare.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-colors"
        >
          + Lägg till kontakt
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-sm text-gray-500">Laddar…</div>
      ) : contacts.length === 0 ? (
        <div className="py-6 text-sm text-gray-500">Inga mail-only-kontakter ännu.</div>
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-200 divide-y divide-gray-100">
          {contacts.map(c => (
            <div key={c._id} className="flex items-center justify-between px-5 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {c.companyName || c.contactPerson || c.email}
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{c.type}</span>
                  {c.isActive === false && <span className="ml-2 text-xs text-gray-400">(inaktiv)</span>}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {c.contactPerson ? `${c.contactPerson} · ` : ''}{c.email}{c.phone ? ` · ${c.phone}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => openEdit(c)} className="text-xs text-blue-600 hover:underline">Redigera</button>
                <button onClick={() => handleDelete(c)} className="text-xs text-red-500 hover:underline">Ta bort</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[1100]" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <ProjektorContactForm
              form={form}
              setForm={setForm}
              onSubmit={handleSave}
              onCancel={() => setShowForm(false)}
              saving={saving}
              error={error}
              isEdit={!!editingId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjektorContactAdmin;
